import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParseKeys } from 'i18next';
import { DateTime } from 'luxon';

import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { useChecksPage } from 'Src/feature/checks/useChecksPage';
import { useDataTable } from 'Src/feature/data-table/useDataTable';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { SelectOptionProps } from 'Src/feature/form/SelectOption';
import { useModalSearchParamControl } from 'Src/feature/modal/useModalControl';
import { useOrderType } from 'Src/feature/order-type/useOrderType';
import { useRevenueCenters } from 'Src/feature/revenue-center/useRevenueCenter';
import { SearchComparator as Comp } from 'Src/models/queryOpts';

import { useCheckListDefaultFilters } from './useCheckListDefaultFilters';
import { useFilterSettingsStore } from './useFilterSettingsStore';

const filterEquals = (a: (string | number)[], b: (string | number)[]) => Array.from(a).sort().join(',') === Array.from(b).sort().join(',');

const useCheckList = () => {
  const { filterSettings, setFilterSettings, resetFilterSettings } = useFilterSettingsStore();

  const checkHeaderCollection = useCheckHeader.query.collection();
  const checksPage = useChecksPage();
  const { t } = useTranslation();
  const employeesGetAll = useEmployee.query.getAll();
  const employeesClockedIn = useEmployee.query.clockedInList();
  const { data: orderTypes } = useOrderType.query.list();
  const { data: revenueCenters } = useRevenueCenters.query.getAll();

  const defaultValues = useCheckListDefaultFilters();

  const [searchVal, setSearchVal] = useState('');

  // make an employee list which consists of all clocked in employees and any employee who created a check
  const employees = Array.from(employeesClockedIn.data ?? []);
  checkHeaderCollection.data?.all.forEach((ch) => {
    if (employees.findIndex((emp) => emp.id === ch.employeeId) >= 0) return;
    const emp = employeesGetAll.data?.find((emp) => emp.id === ch.employeeId);
    if (emp) employees.push(emp);
  });

  const preFilteredCheckHeaders = Array.from(checkHeaderCollection.data?.all || []).filter((ch) => {
    if (checksPage.currentSelection === 'current') return !ch.scheduledAt;
    else if (checksPage.currentSelection === 'scheduled') return !!ch.scheduledAt;
    else if (checksPage.currentSelection === 'searchMode' && searchVal.length > 0) {
      const sv = searchVal.toLowerCase();
      return ch.label.toLowerCase().includes(sv) ||
        ch.number.toLowerCase().includes(sv) ||
        ch.employeeShortName.toLowerCase().includes(sv) ||
        ch.total?.toString().includes(sv) ||
        ch.tableName?.toLowerCase().includes(sv);
    }
    return true; // fail safe, no pre filtering
  });

  const table = useDataTable({
    defaultPerPage: 100000, // effectivly infinity
    // NOTE: defaultSortOrderBy is only set the to the indicated value the first time we call useDataTable. That is because under the hood it
    // is being saved into a useState call. So while this is useful to set the sort for the firs time we render the check list, if our
    // current selection changes, we need to update that sort order. We do this below (see "BK 001").
    defaultSortOrderBy:
      checksPage.currentSelection === 'scheduled'
        ? 'scheduledAt'
        : checksPage.currentSelection === 'current'
          ? 'timeOpen'
          : checksPage.currentSelection === 'searchMode'
            ? 'number'
            : undefined,
    data: preFilteredCheckHeaders.map((ch) => ({
      ...ch,
      timeOpen: Math.abs(DateTime.fromMillis(ch.openedAt ?? 0).diffNow().toMillis()),
      orderChannel: t([`orderChannel.names.${ch.orderChannelId}` as ParseKeys<'translation'>, 'labels.channelUnknown']),
      revCenter: revenueCenters?.find((rc) => rc.id === ch.revenueCenterId)?.name,
      orderType: orderTypes?.find((ot) => ot.id === ch.orderTypeId)?.name,
    })),
  });

  const currentValues = {
    status: (table.store.filter.find('status')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
    employeeId: (table.store.filter.find('employeeId')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
    orderTypeId: (table.store.filter.find('orderTypeId')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
    revenueCenterId: (table.store.filter.find('revenueCenterId')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
    orderChannelId: (table.store.filter.find('orderChannelId')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
  };

  const options: Record<string, SelectOptionProps[]> = {
    status: [
      {
        value: 'all',
        label: t('orderStatus.labels.all'),
        selected: currentValues.status.includes('all'),
      },
      { value: 'Opened', label: t('labels.open') },
      { value: 'Closed', label: t('labels.closed') },
      { value: 'Voided', label: t('labels.voided') },
      { value: 'MergeVoided', label: t('labels.merged') },
    ],
    employeeId: [
      {
        value: 'all',
        label: t('employee.labels.all'),
        selected: currentValues.employeeId.includes('all'),
      },
      ...(employees || [])
        .sort((a, b) => {
          const a_fullName = a.firstName + a.lastName;
          const b_fullName = b.firstName + b.lastName;
          return a_fullName.localeCompare(b_fullName);
        })
        .map((employee) =>
          ({ value: employee.id, label: t('labels.util.employeeFullName', { firstName: employee.firstName, lastName: employee.lastName.charAt(0) }) })),
    ],
    orderTypeId: [
      {
        value: 'all',
        label: t('orderType.all'),
        selected: currentValues.orderTypeId.includes('all'),
      },
      ...(orderTypes || []).sort((a, b) => a.name.localeCompare(b.name)).map(({ id, name }) => ({ value: id as number, label: name })),
    ],
    revenueCenterId: [
      {
        value: 'all',
        label: t('revenueCenter.labels.all'),
        selected: currentValues.revenueCenterId.includes('all'),
      },
      ...(Array.from(revenueCenters || []).map(({ id, name }) => ({ value: id, label: name }))),
    ],
    orderChannelId: [
      {
        value: 'all',
        label: t('orderChannel.labels.all'),
        selected: currentValues.orderChannelId.includes('all'),
      },
      ...[
        { value: 1, label: t('orderChannel.names.1') },
        { value: 2, label: t('orderChannel.names.2') },
        { value: 3, label: t('orderChannel.names.3') },
        { value: 4, label: t('orderChannel.names.4') },
        { value: 5, label: t('orderChannel.names.5') },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    ]
  };

  const defaultFilters = {
    status: {
      value: defaultValues.status.join(','),
      operator: Comp.in,
    },
    employeeId: {
      value: defaultValues.employeeId.join(','),
      operator: Comp.in,
    },
    orderTypeId: {
      value: defaultValues.orderTypeId.join(','),
      operator: Comp.in,
    },
    revenueCenterId: {
      value: defaultValues.revenueCenterId.join(','),
      operator: Comp.in,
    },
    orderChannelId: {
      value: defaultValues.orderChannelId.join(','),
      operator: Comp.in,
    },
  };

  table.store.filter.createMany([
    { field: 'status', ...defaultFilters.status },
    { field: 'employeeId', ...defaultFilters.employeeId },
    { field: 'orderTypeId', ...defaultFilters.orderTypeId },
    { field: 'revenueCenterId', ...defaultFilters.revenueCenterId },
    { field: 'orderChannelId', ...defaultFilters.orderChannelId },
  ]);

  // There are three scenarios that result in filters needing to be enabled/disabled...
  //   1. First time we come to this page, we have made the filters values, but we don't know which selection mode we are in
  //   2. The selection mode has changed
  //   3. A filter was updated which could have been to select/deselect the 'all' option
  // In each case we run the following which will update the filters and re-run the render function... but 'updateMany' is smart
  // and only changes state if there is an actual update.
  table.store.filter.updateMany([
    { field: 'status', isDisabled: currentValues.status.includes('all') || checksPage.currentSelection === 'scheduled' || checksPage.currentSelection === 'searchMode' },
    { field: 'employeeId', isDisabled: currentValues.employeeId.includes('all') || checksPage.currentSelection === 'scheduled' || checksPage.currentSelection === 'searchMode' },
    { field: 'orderTypeId', isDisabled: currentValues.orderTypeId.includes('all') },
    { field: 'revenueCenterId', isDisabled: currentValues.revenueCenterId.includes('all') },
    { field: 'orderChannelId', isDisabled: currentValues.orderChannelId.includes('all') || checksPage.currentSelection === 'current' },
  ]);

  const updateFilter = (field: keyof typeof currentValues, newValues: (string | number)[]) => {
    const prevValues = (table.store.filter.find(field)?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val));
    const allPrevSelected = prevValues.includes('all');
    const allCurrentlySelected = newValues.includes('all');
    const allToggled = (allPrevSelected && !allCurrentlySelected) || (!allPrevSelected && allCurrentlySelected);
    if (newValues.length === 0) newValues = ['all'];
    else if (allToggled) newValues = ['all'];
    else {
      const allIdx = newValues.findIndex((val) => val === 'all');
      if (allIdx >= 0) newValues.splice(allIdx, 1);
    }
    table.store.filter.updateOne(field, { value: newValues.join(',') });
    setFilterSettings({ [field]: newValues });
  };

  const filterHandlers = {
    status: (newValues: (string | number)[]) => updateFilter('status', newValues),
    employeeId: (newValues: (string | number)[]) => updateFilter('employeeId', newValues),
    orderTypeId: (newValues: (string | number)[]) => updateFilter('orderTypeId', newValues),
    revenueCenterId: (newValues: (string | number)[]) => updateFilter('revenueCenterId', newValues),
    orderChannelId: (newValues: (string | number)[]) => updateFilter('orderChannelId', newValues),
  };

  const handleReset = () => {
    resetFilterSettings();
  };

  useEffect(() => {
    table.store.filter.updateMany([
      { field: 'status', value: filterSettings.status.join(',') },
      { field: 'employeeId', value: filterSettings.employeeId.join(',') },
      { field: 'orderTypeId', value: filterSettings.orderTypeId.join(',') },
      { field: 'revenueCenterId', value: filterSettings.revenueCenterId.join(',') },
      { field: 'orderChannelId', value: filterSettings.orderChannelId.join(',') },
    ]);
  }, [filterSettings, table.store.filter]);

  const showReset = (!filterEquals(defaultValues.status, currentValues.status) && checksPage.currentSelection === 'current')
    || (!filterEquals(defaultValues.employeeId, currentValues.employeeId) && checksPage.currentSelection === 'current')
    || !filterEquals(defaultValues.orderTypeId, currentValues.orderTypeId)
    || !filterEquals(defaultValues.revenueCenterId, currentValues.revenueCenterId)
    || (!filterEquals(defaultValues.orderChannelId, currentValues.orderChannelId) && (checksPage.currentSelection === 'scheduled' || checksPage.currentSelection === 'searchMode'));

  // NOTE (BK 001)
  // When the current selection changes, we need to set the default sorting up for that view. Doing this during render (as suggested here: )
  // to avoid a "blip" if we did this with an effect.
  const [prevChecksPageSelection, setPrevChecksPageSelection] = useState(checksPage.currentSelection);
  if (prevChecksPageSelection !== checksPage.currentSelection) {
    setPrevChecksPageSelection(checksPage.currentSelection);
    if (checksPage.currentSelection === 'scheduled') table.store.sort.update({ order: 'asc', orderBy: 'scheduledAt' });
    else if (checksPage.currentSelection === 'current') table.store.sort.update({ order: 'asc', orderBy: 'timeOpen' });
    else if ((checksPage.currentSelection === 'searchMode')) table.store.sort.update({ order: 'asc', orderBy: 'number' });
  }

  const renderOverrides = {
    status: currentValues['status'].includes('all') ? () => t('orderStatus.labels.all') : undefined,
    employeeId: currentValues['employeeId'].includes('all') ? () => t('employee.labels.all') : undefined,
    orderTypeId: currentValues['orderTypeId'].includes('all') ? () => t('orderType.all') : undefined,
    revenueCenterId: currentValues['revenueCenterId'].includes('all') ? () => t('revenueCenter.labels.all') : undefined,
    orderChannelId: currentValues['orderChannelId'].includes('all') ? () => t('orderChannel.labels.all') : undefined,
  };

  // Business requirement is that while in search mode, the drop down filters are "their own thing". So filter selections
  // that were done during "current" or "scheduled" view do not carry over. Additionally, those selections need to be restored
  // when we come back from search mode.
  const searchModeBackupData = useRef({
    checksPageSelection: checksPage.currentSelection,
    statusFilter: table.store.filter.find('status')?.value,
    employeeIdFilter: table.store.filter.find('employeeId')?.value,
    orderTypeIdFilter: table.store.filter.find('orderTypeId')?.value,
    revenueCenterIdFilter: table.store.filter.find('revenueCenterId')?.value,
    orderChannelIdFilter: table.store.filter.find('orderChannelId')?.value,
  });
  const enableSearchMode = () => {
    if (checksPage.currentSelection === 'searchMode') return;
    // backup current selection and filters
    searchModeBackupData.current.checksPageSelection = checksPage.currentSelection;
    searchModeBackupData.current.statusFilter = table.store.filter.find('status')?.value;
    searchModeBackupData.current.employeeIdFilter = table.store.filter.find('employeeId')?.value;
    searchModeBackupData.current.orderTypeIdFilter = table.store.filter.find('orderTypeId')?.value;
    searchModeBackupData.current.revenueCenterIdFilter = table.store.filter.find('revenueCenterId')?.value;
    searchModeBackupData.current.orderChannelIdFilter = table.store.filter.find('orderChannelId')?.value;
    // do the things that will switch us to search mode correctly
    setSearchVal('');
    handleReset();
    checksPage.setCurrentSelection('searchMode');
  };
  const disableSearchMode = () => {
    checksPage.setCurrentSelection(searchModeBackupData.current.checksPageSelection);
    table.store.filter.updateMany([
      { field: 'status', value: searchModeBackupData.current.statusFilter },
      { field: 'employeeId', value: searchModeBackupData.current.employeeIdFilter },
      { field: 'orderTypeId', value: searchModeBackupData.current.orderTypeIdFilter },
      { field: 'revenueCenterId', value: searchModeBackupData.current.revenueCenterIdFilter },
      { field: 'orderChannelId', value: searchModeBackupData.current.orderChannelIdFilter },
    ]);
  };

  return {
    modal: {
      // sharing a single key("modal") in the search params for multiple modals ("edit-tip-list" & "edit-tip")
      editTipList: useModalSearchParamControl({ value: 'edit-tip-list' }),
    },
    table,
    options,
    currentValues,
    filterHandlers,
    renderOverrides,
    handleReset,
    showReset,
    searchVal,
    setSearchVal,
    enableSearchMode,
    disableSearchMode,
  };
};

export { useCheckList };
